import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tune } from "@mui/icons-material";
import { divisionMappingsSimple, roles } from "../../Constants";
import { getLftDivisionFilterKey } from "../../utils/divisionUtils";
import { LFT } from "../../models/LFT";
import { getDonationAmount } from '../../services/DonatorService';
import { Donator } from '../../models/Donator';
import styles from './FreeAgentPage2.module.css';

interface FiltersProps {
    players: LFT[];
    setPlayers: (players: LFT[]) => void;
    donators: Donator[];
}

// Age buckets from the redesign. 'all' is translated; the rest are shown literally.
const AGE_BUCKETS = ['all', '18–21', '22–25', '26+'] as const;
type AgeBucket = typeof AGE_BUCKETS[number];

const inAgeBucket = (age: number | null, bucket: AgeBucket): boolean => {
    if (bucket === 'all') return true;
    // Players without a valid age are only shown under "Alle" (can't filter by age)
    if (age === null) return false;
    if (bucket === '18–21') return age >= 18 && age <= 21;
    if (bucket === '22–25') return age >= 22 && age <= 25;
    return age >= 26;
};

// "2 Division" → "2. Division" for chip labels; "Ligaen" unchanged.
const formatDivision = (name: string): string => name.replace(/^(\d) /, '$1. ');

const Filters: React.FC<FiltersProps> = ({ players, setPlayers, donators }) => {
    const { t } = useTranslation('pages');
    const [ageBucket, setAgeBucket] = useState<AgeBucket>('all');
    const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

    const getDivisionPriority = useCallback((division: string): number => {
        // Lower number = higher priority (Ligaen is top)
        const priorities: Record<string, number> = {
            'ligaen': 0,
            '1div': 1,
            '2divA': 2, '2divB': 2,
            '3divA': 3, '3divB': 3, '3divC': 3, '3divD': 3,
            '4divA': 4, '4divB': 4, '4divC': 4, '4divD': 4,
            '5divA': 5, '5divB': 5,
        };
        return priorities[division] ?? 99;
    }, []);

    const sortByDivision = useCallback((players: LFT[]) => {
        players.sort((a, b) => {
            // if both players are without a team, sort by rating
            if (!a.team && !b.team) {
                // if both players have no stats, sort by elo
                if (!a.stats && !b.stats) {
                    return b.account.faceitElo - a.account.faceitElo;
                }
                // if one player has no stats, sort the other player first
                if (!a.stats) {
                    return 1;
                }
                if (!b.stats) {
                    return -1;
                }
            }
            // if one player is without a team, sort the other player first
            if (!a.team) {
                return 1;
            }
            if (!b.team) {
                return -1;
            }
            // if both players have a team, sort by division priority (Ligaen-qualified
            // teams — incl. kval teams that made it — rank at the top)
            const prioA = a.team.playsInLigaen ? 0 : getDivisionPriority(a.team.division);
            const prioB = b.team.playsInLigaen ? 0 : getDivisionPriority(b.team.division);
            return prioA - prioB;
        });
        return players;
    }, [getDivisionPriority]);

    const sortByDonationsAndPremium = useCallback((players: LFT[]) => {
        players.sort((a, b) => {
            const donationA = getDonationAmount(a.account.steamID, donators);
            const donationB = getDonationAmount(b.account.steamID, donators);

            // First priority: Donations of 25+
            if (donationA >= 25 && donationB >= 25) {
                return donationB - donationA; // Higher donation first
            }
            if (donationA >= 25) return -1;
            if (donationB >= 25) return 1;

            // Second priority: Premium status
            if (a.account.premium && !b.account.premium) {
                return -1;
            }
            if (!a.account.premium && b.account.premium) {
                return 1;
            }

            return 0;
        });
        return players;
    }, [donators]);

    const applyFilters = useCallback(() => {
        let filteredPlayers = players.filter(player => {
            const parsedAge = parseInt(String(player.account.age), 10);
            const age = !isNaN(parsedAge) && parsedAge > 0 ? parsedAge : null;
            const playerDivision = getLftDivisionFilterKey(player.team);
            return inAgeBucket(age, ageBucket) &&
                (selectedDivisions.length === 0 || selectedDivisions.includes(playerDivision)) &&
                (selectedRoles.length === 0 || selectedRoles.includes(player.account.role));
        });
        filteredPlayers = sortByDivision(filteredPlayers);
        filteredPlayers = sortByDonationsAndPremium(filteredPlayers);
        setPlayers(filteredPlayers);
    }, [ageBucket, players, selectedDivisions, selectedRoles, setPlayers, sortByDivision, sortByDonationsAndPremium]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const toggleDivision = (division: string) => {
        setSelectedDivisions(prev =>
            prev.includes(division) ? prev.filter(d => d !== division) : [...prev, division]
        );
    };

    const toggleRole = (role: string) => {
        setSelectedRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const resetFilters = () => {
        setAgeBucket('all');
        setSelectedDivisions([]);
        setSelectedRoles([]);
    };

    return (
        <div className={styles.filterPanel}>
            <div className={styles.filterHeader}>
                <h2 className={styles.filterTitle}>
                    <Tune className={styles.filterTitleIcon} sx={{ fontSize: 19 }} />
                    {t('lft.filters')}
                </h2>
                <button className={styles.resetButton} onClick={resetFilters}>
                    {t('lft.reset')}
                </button>
            </div>

            <div className={styles.filterLabel}>{t('lft.age')}</div>
            <div className={styles.chipRow}>
                {AGE_BUCKETS.map((bucket) => (
                    <button
                        key={bucket}
                        className={`${styles.chip} ${ageBucket === bucket ? styles.chipActive : ''}`}
                        onClick={() => setAgeBucket(bucket)}
                    >
                        {bucket === 'all' ? t('lft.all') : bucket}
                    </button>
                ))}
            </div>

            <div className={styles.filterLabel}>{t('lft.division')}</div>
            <div className={styles.chipRow}>
                {Array.from(divisionMappingsSimple.values()).map((division) => (
                    <button
                        key={division}
                        className={`${styles.chip} ${selectedDivisions.includes(division) ? styles.chipActive : ''}`}
                        onClick={() => toggleDivision(division)}
                    >
                        {formatDivision(division)}
                    </button>
                ))}
            </div>

            <div className={styles.filterLabel}>{t('lft.role')}</div>
            <div className={styles.chipRow}>
                {roles.map((role) => (
                    <button
                        key={role}
                        className={`${styles.chip} ${selectedRoles.includes(role) ? styles.chipActive : ''}`}
                        onClick={() => toggleRole(role)}
                    >
                        {role}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Filters;
